import type {ConnectionOperation, DocumentAdapter} from '@github-ui/optimistic-connection'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {useReorderConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {useQueryClient} from '@github-ui/react-query'
import {reprioritizeMilestoneIssueMutation as reprioritizeMilestoneIssueQueryId} from '@github-ui/server-persisted-queries'
import type {
  ReprioritizeMilestoneIssueInput as GeneratedReprioritizeMilestoneIssueInput,
  ReprioritizeMilestoneIssueMutationMutation,
} from '@github-ui/server-persisted-queries/types/reprioritizeMilestoneIssueMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useEffect, useRef} from 'react'

import {milestoneIssuesPagesDocumentAdapter, milestoneIssuesPagesQueryKey} from '../hooks/use-milestone-issues-pages'

const FAILURE_MESSAGE = 'Failed to reorder milestone item'
const CONNECTION_PATH = ['results']

export type ReprioritizeMilestoneIssueInput = Pick<GeneratedReprioritizeMilestoneIssueInput, 'prevId' | 'timestamp'> & {
  itemId: GeneratedReprioritizeMilestoneIssueInput['id']
}

type GraphQLResponse<T> = {
  readonly data?: T | null
  readonly errors?: ReadonlyArray<{readonly message?: string}> | null
}

export async function reprioritizeMilestoneIssue({
  owner,
  repo,
  number,
  input,
}: {
  owner: string
  repo: string
  number: number
  input: ReprioritizeMilestoneIssueInput
}) {
  const endpoint = `/${owner}/${repo}/milestones/${number}/priority?${new URLSearchParams({
    queryId: reprioritizeMilestoneIssueQueryId,
  })}`
  const response = await reactFetchJSON(endpoint, {
    method: 'PUT',
    body: input,
  })
  if (!response.ok) {
    throw new Error(`${FAILURE_MESSAGE} (${response.status})`)
  }

  let result: GraphQLResponse<ReprioritizeMilestoneIssueMutationMutation>
  try {
    result = (await response.json()) as GraphQLResponse<ReprioritizeMilestoneIssueMutationMutation>
  } catch {
    throw new Error(FAILURE_MESSAGE)
  }

  const payload = result.data?.reprioritizeMilestoneIssue
  if (!payload?.milestone || !payload.orderedItemIds?.length) {
    throw new Error(result.errors?.[0]?.message ?? FAILURE_MESSAGE)
  }

  return {milestone: payload.milestone, orderedItemIds: payload.orderedItemIds}
}

function moveInput(ids: readonly string[], operation: ConnectionOperation) {
  if (operation.type !== 'moveInConnection') throw new Error(FAILURE_MESSAGE)

  const remaining = ids.filter(id => id !== operation.nodeId)
  let insertIndex: number
  if ('at' in operation.anchor) {
    insertIndex = operation.anchor.at === 'start' ? 0 : remaining.length
  } else {
    const anchorId = 'before' in operation.anchor ? operation.anchor.before : operation.anchor.after
    const anchorIndex = remaining.indexOf(anchorId)
    if (anchorIndex === -1) throw new Error(FAILURE_MESSAGE)
    insertIndex = 'before' in operation.anchor ? anchorIndex : anchorIndex + 1
  }

  const nextIds = [...remaining.slice(0, insertIndex), operation.nodeId, ...remaining.slice(insertIndex)]
  return {
    itemId: operation.nodeId,
    nextIds,
    prevId: insertIndex === 0 ? null : (nextIds[insertIndex - 1] ?? null),
  }
}

function confirmedMove(
  orderedItemIds: readonly string[],
  visibleItemIds: readonly string[],
  movedItemId: string,
): ConnectionOperation {
  const visible = new Set(visibleItemIds)
  const orderedVisibleItemIds = orderedItemIds.filter(id => visible.has(id))
  const index = orderedVisibleItemIds.indexOf(movedItemId)
  if (index === -1) throw new Error(FAILURE_MESSAGE)

  const previousId = orderedVisibleItemIds[index - 1]
  const nextId = orderedVisibleItemIds[index + 1]
  return {
    type: 'moveInConnection',
    connectionPath: CONNECTION_PATH,
    nodeId: movedItemId,
    anchor: previousId ? {after: previousId} : nextId ? {before: nextId} : {at: 'start'},
  }
}

export function useReprioritizeMilestoneIssue({
  owner,
  repo,
  number,
  milestoneId,
  milestoneUpdatedAt,
  itemIds,
  closed,
  requestedQuery,
  onError,
}: {
  owner: string
  repo: string
  number: number
  milestoneId: string
  milestoneUpdatedAt: string
  itemIds: readonly string[]
  closed: boolean
  requestedQuery: string | null
  onError: (error: Error) => void
}) {
  const queryClient = useQueryClient()
  const timestampRef = useRef(milestoneUpdatedAt)
  const itemIdsRef = useRef(itemIds)

  useEffect(() => {
    if (Date.parse(milestoneUpdatedAt) > Date.parse(timestampRef.current)) timestampRef.current = milestoneUpdatedAt
  }, [milestoneUpdatedAt])

  const mutation = useReorderConnectionMutation({
    ownerId: milestoneId,
    connectionPath: CONNECTION_PATH,
    scope: {id: `milestone-order:${milestoneId}`},
    mutationFn: async operation => {
      const visibleItemIds = itemIdsRef.current
      const {itemId, prevId} = moveInput(visibleItemIds, operation)
      const payload = await reprioritizeMilestoneIssue({
        owner,
        repo,
        number,
        input: {itemId, prevId, timestamp: timestampRef.current},
      })
      timestampRef.current = payload.milestone.updatedAt
      const visible = new Set(visibleItemIds)
      itemIdsRef.current = payload.orderedItemIds.filter(id => visible.has(id))
      return confirmedMove(payload.orderedItemIds, visibleItemIds, itemId)
    },
    onError,
    onSuccess: confirmedOperation =>
      settleConfirmedOperation({
        queryClient,
        targets: [
          {
            queryKey: milestoneIssuesPagesQueryKey({owner, repo, number, closed, requestedQuery}),
            documentAdapter: milestoneIssuesPagesDocumentAdapter as DocumentAdapter,
          },
        ],
        ownerId: milestoneId,
        operation: confirmedOperation,
      }),
  })

  useEffect(() => {
    if (!mutation.isPending) {
      itemIdsRef.current = itemIds
    }
  }, [itemIds, mutation.isPending])

  return {...mutation, reprioritize: mutation.reorder}
}
